import axios from 'axios';
import { msalInstance } from '../auth';
import { getItem, setItem } from '../storage';
import { parseISO } from 'date-fns';
import { checkValidityBeforeSave, lenientParse, createDateAsUTC, validateFields } from '../../services/items';
import { convertRelativeToDate } from '../../services/utils';

export const getBasisList = async () => {
    const response = await axios.get(`/basis`);
    return response.data;
};

export const copyBasis = async (itemId) => {
    const response = await axios.post(`/basis/${itemId}/copy`);
    return response.data;
};

export const findBasis = (items, id) => {
    return items.filter((i) => i.id === Number(id))[0];
};

export const getBasisById = async (itemId) => {
    const response = await axios.get(`/basis/${itemId}`);
    return response.data;
};

export const editBasis = (itemId, payload) => axios.put(`/basis/${itemId}/update`, payload);

export const deleteBasis = (itemId) => axios.delete(`/basis/${itemId}`);

export const getBasisDropdownOptions = async () => {
    const response = await axios.get(`/basis/dropdown-options`);
    return response.data;
};
export const getBasisScalar = async (itemId, payload) => {
    const response = await axios.post(`/basis/${itemId}/scalar`, payload);
    return response.data;
};
export const modifyScalar = (payload) => axios.put(`/basis/modify-scalar`, payload);

export const deleteAllScalarData = (itemId) => axios.delete(`/basis/${itemId}/scalar/delete-all`);

export const getValidScalarRows = (rows) => {
    return rows
        .map((row) => {
            const { startDate, endDate, ...rest } = row.data;

            const isValid =
                startDate && checkValidityBeforeSave(startDate) && endDate && checkValidityBeforeSave(endDate);

            if (isValid) {
                return {
                    ...rest,
                    startDate: createDateAsUTC(lenientParse(startDate)),
                    endDate: createDateAsUTC(lenientParse(endDate)),
                };
            }

            return null;
        })
        .filter((row) => row !== null);
};

export const columnSchemaOnScalarGrid = {
    startDate: { name: 'startDate', type: 'date', isRequired: true },
    endDate: { name: 'endDate', type: 'date', isRequired: true },
    fullDestination: { name: 'fullDestination', type: 'bool-number' },
    minValue: { name: 'minValue', type: 'number' },
    maxValue: { name: 'maxValue', type: 'number' },
    meanValue: { name: 'meanValue', type: 'number' },
    stdDev: { name: 'stdDev', type: 'number' },
    peakPeriodId: { name: 'peakPeriodId', type: 'string', isRequired: true },
    statUnitId: { name: 'statUnitId', type: 'string', isRequired: true },
};

export const validateParsedNewRowsOnScalarGrid = (parsedData, schema) => {
    return parsedData
        .map((row) => {
            const fields = Object.values(schema).map((v) => ({ ...v, value: row[v.name] }));

            return validateFields(fields);
        })
        .filter((row) => row !== null);
};

export const addScalarRows = async (itemId, payload) => {
    const response = await axios.post(`/basis/${itemId}/scalar/add`, payload);
    return response.data;
};

export const getBasisDartSpread = async (itemId, payload) => {
    const response = await axios.post(`/basis/${itemId}/dart-spreads`, payload);
    return response.data;
};
export const modifyDartSpread = (payload) => axios.put(`/basis/modify-dart-spread`, payload);

export const deleteAllDartSpreadData = (itemId) => axios.delete(`/basis/${itemId}/dart-spread/delete-all`);

export const getValidDartSpreadRows = (rows) => {
    return rows.map((row) => ({ ...row.data })).filter((row) => row !== null);
};

export const columnSchemaOnDartSpreadGrid = {
    month: { name: 'month', type: 'number' },
    year: { name: 'year', type: 'number' },
    peakPeriodLabelId: { name: 'peakPeriodLabelId', type: 'string' },
    multiplier: { name: 'multiplier', type: 'number' },
    adder: { name: 'adder', type: 'number' },
};

export const validateParsedNewRowsOnDartSpreadGrid = (parsedData, schema) => {
    return parsedData
        .map((row) => {
            const fields = Object.values(schema).map((v) => {
                if (v.name === 'year') {
                    return Number(row[v.name]) < 0 ? { ...v, value: '-' } : { ...v, value: row[v.name] };
                } else {
                    return { ...v, value: row[v.name] };
                }
            });

            return validateFields(fields);
        })
        .filter((row) => row !== null);
};

export const addDartSpreadRows = async (itemId, payload) => {
    const response = await axios.post(`/basis/${itemId}/create-dart-spreads`, payload);
    return response.data;
};

export const setBasisFiltersConfig = (config, item) => {
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length) {
        const itemFilters = `${item}Filters`;
        const filters = getItem(itemFilters) || {};

        setItem(itemFilters, {
            ...filters,
            [accounts[0].localAccountId]: config,
        });
    }
};

export const getBasisFiltersConfig = (item) => {
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length) {
        const accountId = accounts[0].localAccountId;
        const filters = getItem(`${item}Filters`);

        if (filters && filters[accountId]) {
            let { startDateFrom, startDateTo, ...rest } = filters[accountId];

            startDateFrom = startDateFrom && parseISO(startDateFrom);
            startDateTo = startDateTo && parseISO(startDateTo);

            return {
                startDateFrom,
                startDateTo,
                ...rest,
            };
        }
    }

    return null;
};

export const systemDefaultBasisFilter = {
    startDateFrom: new Date(2015, 0, 1),
    startDateTo: new Date(2050, 11, 31),
    daysFrom: -21,
    daysTo: 0,
    toggleDaysFrom: false,
    toggleDaysTo: false,
};

export const getBasisDataFilters = (namespacePrefix) => {
    const userPreferences = getBasisFiltersConfig(namespacePrefix);

    if (userPreferences) {
        const filters = getBasisFiltersConfig(namespacePrefix);

        return parseBasisDataFilters(filters);
    } else {
        return {
            startDateFrom: systemDefaultBasisFilter.startDateFrom,
            startDateTo: systemDefaultBasisFilter.startDateTo,
        };
    }
};

export const parseBasisDataFilters = (filters) => {
    return {
        // @todo add guards against partially set filters like this { toggleDaysFrom: true, daysFrom: undefined }
        startDateFrom: filters.toggleDaysFrom ? convertRelativeToDate(filters.daysFrom) : filters.startDateFrom,
        startDateTo: filters.toggleDaysTo ? convertRelativeToDate(filters.daysTo) : filters.startDateTo,
    };
};

export const hasAppliedBasisFilters = (filters) => {
    return (
        filters.startDateFrom.getTime() !== systemDefaultBasisFilter.startDateFrom.getTime() ||
        filters.startDateTo.getTime() !== systemDefaultBasisFilter.startDateTo.getTime()
    );
};
